import { useState, useEffect } from 'react';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/hooks/useAuth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { formatCurrency } from '@/lib/formatters';
import { Wallet, Loader2, AlertCircle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

interface Tenant {
  id: string;
  name: string;
  email: string;
}

interface TenantTopUpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

type Step = 'form' | 'confirm';

const MAX_AMOUNT = 1_000_000;
const MIN_AMOUNT = 0.01;

export function TenantTopUpModal({ open, onOpenChange, onSuccess }: TenantTopUpModalProps) {
  const { user } = useAuth();
  const { post, get, loading } = useApi();

  const [step, setStep] = useState<Step>('form');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      loadTenants();
    }
  }, [open]);

  const loadTenants = async () => {
    setTenantsLoading(true);
    try {
      const response: any = await get('/api/v1/tenants', { limit: 200, status: 'active' });
      const data = response?.data || response || {};
      setTenants(Array.isArray(data.list) ? data.list : []);
    } catch {
      // user sees empty dropdown
    } finally {
      setTenantsLoading(false);
    }
  };

  const selectedTenant = tenants.find((t) => t.id === tenantId);
  const amountValue = parseFloat(amount);

  const validateForm = (): string | null => {
    if (!tenantId) return 'يرجى اختيار التاجر';
    if (!amount || isNaN(amountValue)) return 'يرجى إدخال مبلغ صحيح';
    if (amountValue < MIN_AMOUNT) return `الحد الأدنى للإيداع هو ${formatCurrency(MIN_AMOUNT)}`;
    if (amountValue > MAX_AMOUNT) return `الحد الأقصى للإيداع هو ${formatCurrency(MAX_AMOUNT)}`;
    if (!description.trim()) return 'سبب الإيداع مطلوب لأغراض المراجعة';
    if (description.trim().length < 5) return 'يرجى كتابة سبب واضح (5 أحرف على الأقل)';
    return null;
  };

  const handleReview = (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    setError(null);

    try {
      const result: any = await post(`/api/v1/dashboard/wallet/tenant/${tenantId}/topup`, {
        amount: amountValue,
        description: description.trim(),
        notify: true,           // backend sends in-app + email to tenant
        performed_by: user?.id, // audit trail
      });

      const data = result?.success !== undefined ? result?.data : result;
      if (data) {
        setSuccess(true);
        toast.success(
          `تم إيداع ${formatCurrency(amountValue)} في محفظة ${selectedTenant?.name} بنجاح`
        );
        onSuccess?.();
        setTimeout(() => handleClose(), 1500);
      }
    } catch (err: any) {
      setStep('form');
      setError(err?.message || 'فشل في إيداع الرصيد. يرجى المحاولة مرة أخرى');
    }
  };

  const handleClose = () => {
    if (loading) return;
    setStep('form');
    setTenantId('');
    setAmount('');
    setDescription('');
    setError(null);
    setSuccess(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]" dir="rtl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-950">
              <Wallet className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <DialogTitle>
                {step === 'confirm' ? 'تأكيد الإيداع' : 'إيداع رصيد للتاجر'}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {step === 'confirm'
                  ? 'راجع التفاصيل جيداً قبل التأكيد'
                  : 'اختر التاجر وأدخل المبلغ لإيداعه في محفظته'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* ── STEP 1: Form ── */}
        {step === 'form' && (
          <form onSubmit={handleReview} className="space-y-4">
            {/* Tenant */}
            <div className="space-y-2">
              <Label>
                التاجر <span className="text-destructive">*</span>
              </Label>
              <Select
                value={tenantId}
                onValueChange={(v) => { setTenantId(v); setError(null); }}
                disabled={tenantsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={tenantsLoading ? 'جاري التحميل...' : 'اختر التاجر'} />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span>{t.name}</span>
                      <span className="me-2 text-xs text-muted-foreground">{t.email}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">
                المبلغ <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="amount"
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setError(null); }}
                  className="text-lg font-semibold pr-14"
                  step="0.01"
                  min={MIN_AMOUNT}
                  max={MAX_AMOUNT}
                  dir="ltr"
                  required
                />
              </div>
            </div>

            {/* Preview */}
            {tenantId && amount && amountValue > 0 && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-950">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-700 dark:text-green-300">
                    سيُضاف إلى محفظة {selectedTenant?.name}:
                  </span>
                  <span className="font-bold text-green-600 dark:text-green-400" dir="ltr">
                    {formatCurrency(amountValue)}
                  </span>
                </div>
              </div>
            )}

            {/* Description (required for audit) */}
            <div className="space-y-2">
              <Label htmlFor="description">
                سبب الإيداع <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="description"
                placeholder="مثال: إيداع افتتاحي، تعويض عن مشكلة، رصيد ترحيب..."
                value={description}
                onChange={(e) => { setDescription(e.target.value); setError(null); }}
                rows={2}
                className="resize-none"
                required
              />
              <p className="text-xs text-muted-foreground">
                مطلوب لأغراض المراجعة والتدقيق
              </p>
            </div>

            {/* Notification note */}
            <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
              سيتلقى التاجر إشعاراً داخلياً وبريداً إلكترونياً عند إتمام الإيداع
            </p>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleClose}>
                إلغاء
              </Button>
              <Button type="submit" disabled={!tenantId || !amount || !description.trim()} className="gap-2">
                <Wallet className="h-4 w-4" />
                مراجعة وتأكيد
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* ── STEP 2: Confirm ── */}
        {step === 'confirm' && !success && (
          <div className="space-y-4">
            <Alert className="border-yellow-200 bg-yellow-50 text-yellow-800 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
              <ShieldAlert className="h-4 w-4" />
              <AlertDescription>
                هذه العملية لا يمكن التراجع عنها. تأكد من صحة البيانات قبل المتابعة.
              </AlertDescription>
            </Alert>

            <div className="rounded-lg border bg-muted/30 p-4 space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">التاجر</span>
                <span className="font-semibold">{selectedTenant?.name}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">البريد الإلكتروني</span>
                <span dir="ltr">{selectedTenant?.email}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">المبلغ</span>
                <span className="text-lg font-bold text-green-600" dir="ltr">
                  + {formatCurrency(amountValue)}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">السبب</span>
                <span className="text-start max-w-[60%] text-end">{description.trim()}</span>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground">بواسطة</span>
                <span>{user?.display_name}</span>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setStep('form'); setError(null); }}
                disabled={loading}
              >
                تعديل
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={loading}
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    جاري الإيداع...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    تأكيد الإيداع
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ── Success ── */}
        {success && (
          <div className="py-6 text-center space-y-3">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-950">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <p className="font-semibold">تم الإيداع بنجاح</p>
            <p className="text-sm text-muted-foreground">
              تم إيداع {formatCurrency(amountValue)} في محفظة {selectedTenant?.name} وإرسال الإشعار
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
